import { useState, type ComponentType } from "react";
import { NavLink, useLocation, useSearchParams } from "react-router-dom";
import { useLocalizedNavigate } from "@/hooks/useLocalizedPath";
import { LogOut, Mail, Rocket, Settings, Menu, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { SettingsModal } from "../SettingsModal";
import { ContactUsModal } from "../ContactUsModal";
import { ReferralModal } from "../ReferralModal";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import Logo from "./Logo";
import { ModeToggle } from "../ModeToggle";
import { logout, getMe, clearLoggedInCookie } from "@/api/client";
import { usePostHog } from "posthog-js/react";
import { useT } from "@/lib/translation";

export interface SidebarNavItem {
  to: string;
  end?: boolean;
  icon: ComponentType<{ className?: string }>;
  label: string;
  /** Override the router's own active-match logic (e.g. to also match nested routes). */
  isActive?: (pathname: string, routerIsActive: boolean) => boolean;
}

const baseNavLinkClass = (active: boolean) =>
  `flex items-center gap-3 rounded-lg px-3 py-2 transition-all hover:text-primary ${active ? "bg-muted text-primary" : "text-muted-foreground"}`;

interface SidebarNavProps {
  navItems: SidebarNavItem[];
  roadmapPath: string;
}

export const SidebarNav = ({ navItems, roadmapPath }: SidebarNavProps) => {
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: getMe });
  const isAuthenticated = !!me;
  const location = useLocation();
  const navigate = useLocalizedNavigate();
  const queryClient = useQueryClient();
  const posthog = usePostHog();
  const t = useT();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const referralModalExternalOpen = searchParams.get("modal") === "referrals";

  const handleReferralModalOpened = () => {
    setSearchParams(
      (prev) => {
        prev.delete("modal");
        return prev;
      },
      { replace: true },
    );
  };

  const isRoadmapActive = location.pathname === roadmapPath;
  const roadmapButtonClasses = cn(
    "w-full justify-start mb-2",
    isRoadmapActive && "bg-muted text-primary",
  );

  const handleLogout = async () => {
    await logout();
    posthog?.reset();
    clearLoggedInCookie();
    queryClient.clear();
    navigate("/", { replace: true });
  };

  const renderNavContent = (onNavigate?: () => void) => (
    <div className="flex flex-col h-full">
      <div className="flex h-14 items-center border-b px-6">
        <Logo />
      </div>

      <div className="px-4 pt-5 pb-4">
        <ModeToggle className="w-full" />
      </div>

      <nav className="grid items-start px-4 text-base font-medium gap-y-1">
        {navItems.map((item) => (
          <div key={item.to}>
            <NavLink
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                baseNavLinkClass(
                  item.isActive
                    ? item.isActive(location.pathname, isActive)
                    : isActive,
                )
              }
              onClick={onNavigate}
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          </div>
        ))}
      </nav>

      <div className="mt-auto p-4">
        {isAuthenticated && (
          <ReferralModal
            externalOpen={referralModalExternalOpen}
            onExternalOpenHandled={handleReferralModalOpened}
          >
            <Button variant="ghost" className="w-full justify-start mb-2">
              <Gift className="h-4 w-4 mr-2" />
              {t("common:sidebar.referFriend")}
            </Button>
          </ReferralModal>
        )}
        <Button
          variant="ghost"
          className={roadmapButtonClasses}
          onClick={() => {
            navigate(roadmapPath);
            onNavigate?.();
          }}
        >
          <Rocket className="h-4 w-4 mr-2" />
          {t("common:sidebar.roadmap")}
        </Button>
        <ContactUsModal mode="auth">
          <Button variant="ghost" className="w-full justify-start mb-2">
            <Mail className="h-4 w-4 mr-2" />
            {t("common:sidebar.contactUs")}
          </Button>
        </ContactUsModal>

        {isAuthenticated && (
          <SettingsModal>
            <Button variant="ghost" className="w-full justify-start mb-2">
              <Settings className="h-4 w-4 mr-2" />
              {t("common:sidebar.settings")}
            </Button>
          </SettingsModal>
        )}
        {isAuthenticated && (
          <Button
            variant="ghost"
            className="w-full justify-start"
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4 mr-2" />
            {t("common:sidebar.logout")}
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-10 hidden w-60 flex-col border-r bg-background sm:flex">
        {renderNavContent()}
      </aside>

      {/* Mobile: hamburger button */}
      <Button
        variant="ghost"
        size="icon"
        className="fixed top-3 left-4 z-40 sm:hidden"
        onClick={() => setMobileOpen(true)}
      >
        <Menu className="h-5 w-5" />
        <span className="sr-only">{t("common:sidebar.openNavigation")}</span>
      </Button>

      {/* Mobile: slide-in drawer */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-60">
          {renderNavContent(() => setMobileOpen(false))}
        </SheetContent>
      </Sheet>
    </>
  );
};
